import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { isAdminOrOwner } from './selectors/account';
import { localStorage } from './utils/namespaced-storage';

// TODO: Use this Appshell layout later
// import { AppShell } from './app/shell/AppShell';

import AppShell from './AppShell';
import Main from './views/Main';
import FeatureRollout from './utils/feature-rollout';
import { NODE_ENV, START_PATH } from './constants';
import { useSessionPermissions } from './hooks';
import { SubscriptionSettings } from './views/settings/subscription';
import { SettingsApp } from './views/settings/app';
import { ApplicationSettingsApp } from './mfe/application-settings';
import { PaymentSettings } from './mfe/application-settings/payment-settings';
import { SubscriptionCheckout } from './mfe/subscription/checkout';
import { SubscriptionUpgrade } from './mfe/subscription/upgrade';
import { ContractApp } from './views/contract';
import { UpsellApp } from './views/upsell';

import { NotFoundPage } from './app/pages/NotFoundPage';
import { AccessDeniedPage } from './app/pages/AccessDeniedPage';

import {
  ServicesList,
  EditService,
  EditCourse,
  EditCategory,
  NewService,
  NewCourse,
  NewCategory,
} from './views/services';

import { Home } from './features/home/screens/Home';
import { Beta } from './features/beta/screens/Beta';
import { Developer } from './features/developer/screens/Developer';

const isServicesRefactorActive = FeatureRollout.activate(
  'services-app-refactor',
  { percentage: 0 }
);

/*
  IMPORTANT: The reason we have only one registered route (which is, by the way, a wildcard)
  pointing to the Main component is that we are leveraging the app-shell design, specifically
  the way it loads micro FE apps.

  The improvements consist of two steps:
   1) Deprecating the old way of doing this loading process, which relies on the browser routing
      that is used to listen to route changes, get the new route path and load a micro app matching that
      routing path name. All this logic is now encapsulated into the Main component, which should
      remain untouched from now on;
   2) Start to define new route paths that are associated to a single page component with a well defined
      context and responsibilities set. These page components will use the MicroApp component to load the
      micro apps, and legacy routes should be refactored - on a iterative manner - to use react-router routes
      and have their own individual page components.

  For more information, refer to the ADR link:
  https://www.notion.so/shorewiki/App-Shell-architecture-redesign-a842d1054de44867a9fa9a1228dab64d

*/
let playgroundRoutes = [];

if (NODE_ENV === 'development') {
  const { default: pRoutes } = require('../playground/routes');

  playgroundRoutes = pRoutes;
}

const servicesRoutes = isServicesRefactorActive
  ? [
      {
        path: '/services',
        element: <ServicesList />,
        children: [
          {
            path: 'edit/service/:serviceId',
            element: <EditService />,
          },
          {
            path: 'edit/course/:courseId',
            element: <EditCourse />,
          },
          {
            path: 'edit/category/:categoryId',
            element: <EditCategory />,
          },
          {
            path: 'new/service',
            element: <NewService />,
          },
          {
            path: 'new/course',
            element: <NewCourse />,
          },
          {
            path: 'new/category',
            element: <NewCategory />,
          },
        ],
      },
    ]
  : [];

export const INIT_APP_ROUTES = [
  {
    path: '*',
    element: <AppShell />,
  },
];

function AdminOrOwnerRoute({ children }) {
  const currentAccount = useSelector((state) => state.currentAccount);
  const currentMerchant = useSelector((state) => state.currentMerchant);

  if (
    !isAdminOrOwner({
      merchantId: currentMerchant?.id,
      roles: currentAccount?.roles ?? [],
      organizationAdmin: currentAccount?.organization_admin,
    })
  ) {
    return <Navigate to="/access-denied" />;
  }

  return children;
}

function PrivateRoute({ children }) {
  const sessionPermissions = useSessionPermissions();

  if (sessionPermissions.role && !sessionPermissions.hasPermission) {
    return <Navigate to="/access-denied" />;
  }

  if (sessionPermissions.role && sessionPermissions.hasPermission) {
    return children;
  }

  return null;
}

function RedirectToSubscriptionCheckout() {
  const location = useLocation();

  if (location.pathname !== '/subscription') {
    return <Navigate to="/subscription" />;
  }

  return null;
}

function SuccessCheckout() {
  localStorage.setItem('showCBSubscriptionSuccessModal', 'ON');

  return <Navigate to="/" />;
}

const SUCCESS_SUBSCRIPTION_REDIRECT_ROUTE = {
  path: '/success-checkout',
  element: <SuccessCheckout />,
};

export const INACTIVE_SUBSCRIPTION_ROUTES = [
  {
    element: <AppShell />,
    children: [
      SUCCESS_SUBSCRIPTION_REDIRECT_ROUTE,
      {
        path: '/subscription',
        element: <SubscriptionCheckout />,
      },
      {
        path: '/settings/*',
        element: <SubscriptionSettings />,
      },
      {
        path: '/application-settings/*',
        element: <ApplicationSettingsApp />,
      },
      {
        path: '/developer',
        element: <Developer />,
      },
      {
        path: '*',
        element: <RedirectToSubscriptionCheckout />,
      },
    ],
  },
];

export const ROUTES = [
  {
    element: <AppShell />,
    children: [
      ...playgroundRoutes,
      ...servicesRoutes,
      SUCCESS_SUBSCRIPTION_REDIRECT_ROUTE,
      {
        path: '/',
        element: <Navigate to={START_PATH} />,
      },
      {
        path: '/404',
        element: <NotFoundPage />,
      },
      {
        path: '/contract',
        element: <ContractApp />,
      },
      {
        path: '/upsell/*',
        element: <UpsellApp />,
      },
      {
        path: '/home/*',
        element: <Home />,
      },
      {
        path: '/subscription',
        element: <SubscriptionCheckout />,
      },
      {
        path: '/subscription/upgrade',
        element: <SubscriptionUpgrade />,
      },
      {
        path: '/settings/subscription',
        element: <SubscriptionSettings />,
      },
      {
        path: '/settings/*',
        element: <SettingsApp />,
      },
      {
        path: '/application-settings/payments',
        element: (
          <AdminOrOwnerRoute>
            <PaymentSettings />
          </AdminOrOwnerRoute>
        ),
      },
      {
        path: '/application-settings/*',
        element: <ApplicationSettingsApp />,
      },
      {
        path: '/access-denied',
        element: <AccessDeniedPage />,
      },
      {
        path: '/developer',
        element: <Developer />,
      },
      {
        path: '/beta',
        element: <Beta />,
      },
      // TODO: Test this app later
      // {
      //   path: '/locations',
      //   element: <Locations />,
      // },
      {
        path: '*',
        // This component is DEPRECATED and must remain untouched.
        element: (
          <PrivateRoute>
            <Main />
          </PrivateRoute>
        ),
      },
    ],
  },
];
